import { useEffect, useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { Download, FileText, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { Loader, PDFRenderer, Show } from "components";
import { classNames, useDownloadFile, wrapClick } from "utils";
import useApi from "utils/use-api";
import config from "config";
import AttachFileButton from "./attach-file-button";

type ParticipantDocument = {
  id?: string | number;
  offer_participant_document_id?: string | number;
  offer_published_participant_document_id?: string | number;
  document_id?: string | number;
  file_name?: string;
  document_name?: string;
  name?: string;
  file_path?: string;
  mime_type?: string;
  created_at?: string;
};

type Props = {
  offerId?: string;
  publishedParticipantId?: string;
  reinsurerName?: string;
  /** When set, the matching document is auto-selected for preview. */
  focusFilePath?: string | null;
};

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];

const ASSET_BASE = config.asset.uri.replace(/\/$/, "");

// The `{document}` path param / list shape is not strongly typed by the API,
// so resolve the identifier and label defensively across likely field names.
const getDocId = (doc: ParticipantDocument) =>
  String(
    doc?.offer_published_participant_document_id ??
      doc.document_id ??
      doc.id ??
      doc.file_path ??
      ""
  );

const getDocName = (doc: ParticipantDocument) =>
  doc.file_name ||
  doc.document_name ||
  doc.name ||
  doc.file_path?.split("/").pop() ||
  "Document";

const getDocUrl = (doc: ParticipantDocument) =>
  `${ASSET_BASE}/api/offer-participant-document/${getDocId(doc)}/view`;

const basename = (path?: string | null) =>
  String(path || "")
    .split(/[\\/]/)
    .pop()
    ?.split("?")[0]
    ?.toLowerCase() || "";

// The thread message and the documents list may store the path differently
// (relative vs. absolute), so match on the exact path first, then the file name.
const matchesFocus = (doc: ParticipantDocument, focusFilePath: string) => {
  const target = focusFilePath.toLowerCase();
  const docPath = (doc.file_path || "").toLowerCase();
  if (docPath && docPath === target) return true;
  const focusName = basename(focusFilePath);
  return Boolean(focusName) && basename(getDocName(doc)) === focusName;
};

const getDocType = (doc: ParticipantDocument): "pdf" | "image" | "other" => {
  const ext = getDocName(doc).split(".").pop()?.toLowerCase() || "";
  const mime = (doc.mime_type || "").toLowerCase();
  if (mime.includes("pdf") || ext === "pdf") return "pdf";
  if (mime.includes("image") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  return "other";
};

const ParticipantDocuments = ({
  offerId,
  publishedParticipantId,
  reinsurerName,
  focusFilePath,
}: Props) => {
  const api = useApi();

  const [documents, setDocuments] = useState<ParticipantDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { downloadAction, downloadLoading } = useDownloadFile({
    onError: () => toast.error("Could not download document"),
  });

  const fetchDocuments = async () => {
    if (!publishedParticipantId) return;
    setLoadingList(true);
    try {
      const { data } = await api.get("/api/offer-participant-documents", {
        params: { offer_published_participant_id: publishedParticipantId },
      });
      const list: ParticipantDocument[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.documents)
            ? data.documents
            : [];
      setDocuments(list);
    } catch (error) {
      toast.error("Could not load attachments");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedParticipantId]);

  // Keep a valid document selected for preview as the list changes. A focused
  // file (e.g. clicked from the message thread) takes priority when present.
  useEffect(() => {
    if (!documents.length) {
      setSelectedId(null);
      return;
    }
    if (focusFilePath) {
      const focused = documents.find((doc) => matchesFocus(doc, focusFilePath));
      if (focused) {
        setSelectedId(getDocId(focused));
        return;
      }
    }
    setSelectedId((prev) =>
      prev && documents.some((doc) => getDocId(doc) === prev)
        ? prev
        : getDocId(documents[0])
    );
  }, [documents, focusFilePath]);

  const handleDownload = (doc: ParticipantDocument) => () => {
    if (!getDocId(doc)) return;
    downloadAction(getDocUrl(doc), getDocName(doc));
  };

  const handleDelete = (doc: ParticipantDocument) => async () => {
    const id = getDocId(doc);
    if (!id) return;
    setDeletingId(id);
    try {
      await api.post(`/api/offer-participant-document/${id}/delete`);
      toast.success("Document removed");
      setDocuments((prev) => prev.filter((item) => getDocId(item) !== id));
    } catch (error) {
      toast.error("Could not remove document");
    } finally {
      setDeletingId(null);
    }
  };

  const selectedDoc = documents.find((doc) => getDocId(doc) === selectedId);

  const renderPreview = (doc: ParticipantDocument) => {
    const type = getDocType(doc);
    const url = getDocUrl(doc);

    if (type === "pdf") {
      return (
        <div className="flex-1 min-h-[400px]">
          <PDFRenderer url={url} reloadToken={getDocId(doc)} />
        </div>
      );
    }
    if (type === "image") {
      return (
        <div className="flex items-center justify-center bg-gray-50 rounded-md p-3">
          <img
            src={url}
            alt={getDocName(doc)}
            className="max-h-[60vh] w-auto object-contain"
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
        <FileText className="w-12 h-12 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">
          Preview isn't available for this file type.
        </p>
        <button
          type="button"
          onClick={wrapClick(handleDownload(doc))}
          disabled={downloadLoading}
          className="mt-4 inline-flex items-center gap-1 rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Download className="w-4 h-4" />
          Download to view
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <span className="text-sm text-gray-500 truncate">
          {reinsurerName
            ? `Documents attached for ${reinsurerName}`
            : "Documents attached to this participant"}
        </span>
        <AttachFileButton
          offerId={offerId}
          publishedParticipantId={publishedParticipantId}
          onUploaded={fetchDocuments}
        />
      </div>

      {loadingList ? (
        <Loader text="Loading attachments..." />
      ) : documents.length ? (
        <div className="flex flex-1 min-h-0 gap-4 p-4">
          {/* File list */}
          <aside className="w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 pr-2">
            <ul className="space-y-1">
              {documents.map((doc) => {
                const id = getDocId(doc);
                const active = id === selectedId;
                return (
                  <li key={id}>
                    <div
                      onClick={() => setSelectedId(id)}
                      className={classNames(
                        active
                          ? "bg-primary-50 text-primary-700"
                          : "hover:bg-gray-50 text-gray-700",
                        "group flex items-center justify-between gap-2 rounded-md px-2 py-2 cursor-pointer"
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        <span className="truncate text-sm font-medium">
                          {getDocName(doc)}
                        </span>
                      </div>
                      <button
                        type="button"
                        title="Remove"
                        disabled={deletingId === id}
                        onClick={wrapClick(handleDelete(doc))}
                        className="flex-shrink-0 text-gray-400 hover:text-red-600 disabled:cursor-not-allowed"
                      >
                        <Show if={deletingId !== id}>
                          <Trash2 className="w-4 h-4" />
                        </Show>
                        <Show if={deletingId === id}>
                          <span className="text-xs">...</span>
                        </Show>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Preview pane */}
          <section className="flex-1 min-w-0 flex flex-col">
            {selectedDoc ? (
              <>
                <div className="flex items-center justify-between gap-2 pb-3 mb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {getDocName(selectedDoc)}
                  </span>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      type="button"
                      disabled={downloadLoading}
                      onClick={wrapClick(handleDownload(selectedDoc))}
                      className="text-primary-600 disabled:cursor-not-allowed hover:underline flex items-center gap-1 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(getDocUrl(selectedDoc), "_blank")
                      }
                      className="text-gray-600 hover:underline flex items-center gap-1 text-sm"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      Open
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  {renderPreview(selectedDoc)}
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                Select a document to preview.
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 py-12 text-center">
          <FileText className="w-10 h-10 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">
            No documents attached yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default ParticipantDocuments;
